import { SplideSlide } from '@splidejs/react-splide'
import React, { useRef, useEffect } from 'react'
import ReactECharts from 'echarts-for-react'
import HomeFutureMineralsPricesChange from './HomeFutureMineralsPricesChange'
import moment from 'moment'
import { getPriceChangeValue } from '../../../config/global'
import { Link } from 'react-router-dom'

export default function HomeFutureLastMonthPricesChart({ mineral, lng }) {
  const chartRef = useRef(null)

  // Force chart resize after mounting
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.getEchartsInstance().resize()
    }
  }, [])

  // Prepare the chart option for the single chart
  const getChartOption = (prices, dates, change, unit) => {
    const minPrice = Math.min(...prices)
    const maxPrice = Math.max(...prices)
    const priceRange = maxPrice - minPrice
    const safePriceRange = priceRange === 0 ? Math.abs(minPrice) || 1 : priceRange
    const orderOfMagnitude = Math.pow(10, Math.floor(Math.log10(safePriceRange)))
    const stepSize = orderOfMagnitude / 10
    const adjustedMin = minPrice - stepSize
    const adjustedMax = maxPrice + stepSize

    return {
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates,
        show: false,
      },
      yAxis: {
        type: 'value',
        min: adjustedMin,
        max: adjustedMax,
        interval: stepSize,
        splitNumber: 5,
        show: false,
      },
      series: [
        {
          data: prices,
          type: 'line',
          smooth: true, // Enable smooth line
          symbol: 'none', // Ensure no markers on data points
          areaStyle: {
            color: `${getPriceChangeValue(change)}5a`,
          },
          lineStyle: {
            color: getPriceChangeValue(change),
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: ({ 0: { value, name } }) => {
          return `
            <div class="flex flex-col gap-1">
              <div class="flex flex-row-reverse gap-2 items-end">
                <div class="font-bold">Date</div>
                <div>${moment(name).format('MMMM Do YYYY')}</div>
              </div>
              <div class="flex flex-row-reverse gap-2 items-end">
                <div class="font-bold">Price</div>
                  <div dir="ltr">$ ${Number.isInteger(value) ? value.toFixed(2) : value} / ${unit}</div>
              </div>
            </div>
          `
        },
      },
      grid: {
        left: '0%',
        right: '0%',
        bottom: '0%',
        top: '10%',
        containLabel: false,
      },
    }
  }

  return (
    <SplideSlide
      key={mineral.id}
      className="relative cursor-pointer overflow-hidden border-2 border-muted-300 bg-white text-center hover:border-secondary-600 hover:bg-muted-300"
    >
      <Link
        to={`/${lng}/minerals/${mineral.slug}/prices?CHART=AREA&INTERVAL=MONTH`}
        className="flex flex-col justify-between p-2"
      >
        <div className="flex items-start justify-between ltr:flex-row-reverse">
          <div className="flex gap-2 ltr:flex-row-reverse ltr:text-end rtl:text-start">
            <div className="border-2 border-muted-300 bg-light p-1">
              <img
                className="w-10"
                src={`${process.env.REACT_APP_API_BASE_URL}/storage/minerals/${mineral.image}`}
                alt={mineral[`name_${lng}`]}
              />
            </div>
            <div className="font-bold">
              <div>{mineral[`name_${lng}`].replace('(', '').replace(')', '')}</div>
              <div className="text-secondary-600">{mineral.price} $</div>
            </div>
          </div>
          <HomeFutureMineralsPricesChange change={mineral.change} />
        </div>
        <div className="mt-4 flex !h-full">
          <ReactECharts
            ref={chartRef}
            option={getChartOption(mineral.prices, mineral.dates, mineral.change, mineral[`unit_symbol`])}
            className="!max-h-32 w-full"
          />
        </div>
      </Link>
    </SplideSlide>
  )
}
